import { useEffect } from 'react';
import facebookIcon from '../../assets/helpers/oAuth/facebookIcon.svg';
import googleIcon from '../../assets/helpers/oAuth/googleIcon.svg';
import microsoftIcon from '../../assets/helpers/oAuth/microsoftIcon.svg';
import appleIcon from '../../assets/helpers/oAuth/appleIcon.svg';
import styles from './OAuth.module.css';
import { useDescope } from '@descope/react-sdk';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DescopeProvider, SdkResponse, URLResponse } from '../../interfaces/descope';
import { useAuth } from '../../auth/Auth';
import { AuthModel } from '../../auth/_models';
import { getUserByToken, oAuth } from '../../auth/_requests';
import { postLoginTask } from '../utils/login';
import toast from 'react-hot-toast';
import { logThirdPartyError } from '../utils/safeSentryError';
import {
  clearSignupTermsAccepted,
  markStartPayRentSetup,
  saveSignupTermsConsent,
  wasSignupTermsAccepted,
} from '../utils/signupConsent';
import { setAnalyticsUser, trackLogin, trackSignUp, trackSignUpStart } from '../utils/analytics';

interface OAuthProps {
  /**
   * Function to toggle the loading state in the parent component.
   * @param value - A boolean indicating whether loading is active.
   */
  setLoading: (value: boolean) => void;
}

const OAuth: React.FC<OAuthProps> = ({ setLoading }) => {
  const descope = useDescope();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setCurrentUser, saveAuth } = useAuth();
  const navigate = useNavigate();
  const { referralCode } = useParams();

  // Social login button do not work after the first time,
  // so we need to reload the page to reset the descope button
  const handleDescopeButtonReset = () => {
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  const handleOAuthCallback = async () => {
    const code = searchParams.get('code');
    if (!code) {
      return;
    }
    setLoading(true);
    removeParamsFromUrl();
    try {
      const exchangeResponse = await descope.oauth.exchange(code);
      if (!exchangeResponse.ok) {
        const error = new Error(
          exchangeResponse.error?.errorDescription || 'Failed to exchange code'
        );
        logThirdPartyError(error, {
          method: 'descope.oauth.exchange',
          errorDescription: exchangeResponse.error?.errorDescription,
          errorCode: exchangeResponse.error?.errorCode,
        });
        throw error;
      }
      const sessionToken = descope.getSessionToken();
      if (!sessionToken) {
        const error = new Error('Failed to get session token');
        logThirdPartyError(error, {
          method: 'descope.getSessionToken',
        });
        throw error;
      }
      const userDetail = await oAuth(sessionToken, referralCode);
      if (!userDetail || !userDetail.data) {
        return;
      }
      const auth: AuthModel = {
        message: userDetail.message,
        data: {
          tenantId: userDetail.data.tenantId,
          token: userDetail.data.token,
        },
      };
      saveAuth(auth);
      const { data: user } = await getUserByToken(userDetail.data.token);
      setCurrentUser(user);
      localStorage.setItem('isSignUp', 'true');

      setAnalyticsUser(user.user?.userId, undefined, userDetail.data.tenantId);
      if (userDetail.data.isNewUser) {
        trackSignUpStart('oauth');
        trackSignUp('oauth');
      } else {
        trackLogin('oauth');
      }

      if (userDetail.data.isNewUser && wasSignupTermsAccepted()) {
        await saveSignupTermsConsent(userDetail.data.token);
        clearSignupTermsAccepted();
        markStartPayRentSetup();
      } else {
        clearSignupTermsAccepted();
      }

      if (user.user?.isEmailVerified !== 1) {
        navigate('/verify-email');
        return;
      }
      await postLoginTask(auth, navigate, saveAuth);
    } catch (error: any) {
      const isBlockedPhone =
        error instanceof Error && error.message === 'phone_number_blocked';
      // Log error to Sentry if not already logged (errors are logged above before throwing)
      if (
        !isBlockedPhone &&
        error instanceof Error &&
        !error.message.includes('Failed to exchange code') &&
        !error.message.includes('Failed to get session token') &&
        !error.message.includes('Failed to start OAuth flow') &&
        !error.message.includes('No redirect URL')
      ) {
        logThirdPartyError(error, {
          method: 'descope.oauth.exchange',
        });
      }
      toast.error(
        isBlockedPhone
          ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
          : error?.message
      );
      saveAuth(undefined);
      setCurrentUser(undefined);
      handleDescopeButtonReset();
    } finally {
      setLoading(false);
    }
  };

  const removeParamsFromUrl = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('code');
    nextParams.delete('state');
    setSearchParams(nextParams, { replace: true });
  };

  // Handle OAuth callback when code is received
  useEffect(() => {
    handleOAuthCallback();
  }, [searchParams]);

  const getOAuthUrl = async (provider: DescopeProvider): Promise<SdkResponse<URLResponse>> => {
    const redirectUrl = window.location.origin;
    switch (provider) {
      case 'google':
        return await descope.oauth.start.google(redirectUrl);
      case 'apple':
        return await descope.oauth.start.apple(redirectUrl);
      case 'microsoft':
        return await descope.oauth.start.microsoft(redirectUrl);
      case 'facebook':
        return await descope.oauth.start.facebook(redirectUrl);
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  };

  const handleOAuthRedirect = async (provider: DescopeProvider) => {
    setLoading(true);
    try {
      const response = await getOAuthUrl(provider);
      if (!response.ok) {
        const error = new Error(response.error?.errorDescription || 'Failed to start OAuth flow');
        logThirdPartyError(error, {
          method: `descope.oauth.start.${provider}`,
          errorDescription: response.error?.errorDescription,
        });
        throw error;
      }
      const authUrl = response.data?.url;
      if (!authUrl) {
        const error = new Error('No redirect URL returned from Descope');
        logThirdPartyError(error, {
          method: `descope.oauth.start.${provider}`,
        });
        throw error;
      }
      window.location.href = authUrl;
    } catch (error: any) {
      // Log error to Sentry if not already logged
      if (
        error instanceof Error &&
        !error.message.includes('Failed to start OAuth flow') &&
        !error.message.includes('No redirect URL')
      ) {
        logThirdPartyError(error, {
          method: `descope.oauth.start.${provider}`,
        });
      }
      toast.error(error?.message);
      console.error('OAuth start error:', error);
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        <button
          onClick={() => handleOAuthRedirect('google')}
          className={styles.iconButton}
          aria-label='Login with Google'
        >
          <img src={googleIcon} alt='Google Icon' className={styles.icon} />
        </button>
        <button
          onClick={() => handleOAuthRedirect('apple')}
          className={styles.iconButton}
          aria-label='Login with Apple'
        >
          <img src={appleIcon} alt='Apple Icon' className={styles.icon} />
        </button>
        <button
          onClick={() => handleOAuthRedirect('microsoft')}
          className={styles.iconButton}
          aria-label='Login with Microsoft'
        >
          <img src={microsoftIcon} alt='Microsoft Icon' className={styles.icon} />
        </button>
        <button
          onClick={() => handleOAuthRedirect('facebook')}
          className={styles.iconButton}
          aria-label='Login with Facebook'
        >
          <img src={facebookIcon} alt='Facebook Icon' className={styles.icon} />
        </button>
      </div>
    </div>
  );
};

export default OAuth;
